/**
 * WebSocket Service (Socket.IO)
 * ============================================================================
 *
 * Purpose:
 * --------
 * Socket.IO client service using functional programming principles.
 * Provides WebSocket connection management and message handling.
 *
 * Design Principles:
 * ------------------
 * - **Functional programming** (no classes!)
 * - Event-driven architecture
 * - Auto-reconnection with exponential backoff
 * - Post-connect token authentication
 * ============================================================================
 */

import { CONNECTION_STATUS, REALTIME_CONFIG, SOCKET_EVENT, getWebSocketUrl } from './constants';
import type {
  ConnectionState,
  EventHandler,
  EventPayload,
  SocketMessage,
  WebSocketOptions,
} from './types';
import { Socket, io } from 'socket.io-client';

import { getAccessToken } from '../auth';

/** Internal state */
let socket: Socket | null = null;
let connectionState: ConnectionState = {
  status: CONNECTION_STATUS.DISCONNECTED,
  isConnected: false,
  isConnecting: false,
  isReconnecting: false,
  reconnectAttempts: 0,
  error: null,
  connectedAt: null,
  disconnectedAt: null,
  lastPing: null,
  lastPong: null,
  latency: null,
};
let options: WebSocketOptions = {};
const eventHandlers = new Map<string, Set<EventHandler>>();
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Initialize Socket Service
 */
export const initialize = (opts: WebSocketOptions): void => {
  options = {
    ...REALTIME_CONFIG,
    url: opts.url || getWebSocketUrl(),
    ...opts,
  };

  if (options.debug) {
    console.log('[Socket] Initialized with options:', options);
  }

  if (options.autoConnect) {
    connect();
  }
};

/**
 * Connect to Socket.IO server
 */
export const connect = (): Promise<void> => new Promise((resolve, reject) => {
    if (socket?.connected) {
      if (options.debug) {
        console.log('[Socket] Already connected');
      }
      resolve();
      return;
    }

    if (connectionState.isConnecting) {
      if (options.debug) {
        console.log('[Socket] Already connecting');
      }
      return;
    }

    updateConnectionState({
      status: CONNECTION_STATUS.CONNECTING,
      isConnecting: true,
      error: null,
    });

    const token = options.token || getAccessToken() || undefined;
    const url = options.url || getWebSocketUrl();

    try {
      socket = io(url, {
        auth: { token },
        transports: ['websocket', 'polling'],
        autoConnect: false,
        reconnection: false, // Manual reconnection for consistent behavior
        timeout: options.connectionTimeout ?? REALTIME_CONFIG.CONNECTION_TIMEOUT,
      });

      const connectTimeout = setTimeout(() => {
        if (!socket?.connected) {
          socket?.disconnect();
          const err = new Error('Connection timeout');
          updateConnectionState({
            status: CONNECTION_STATUS.ERROR,
            isConnecting: false,
            error: err.message,
          });
          reject(err);
        }
      }, options.connectionTimeout ?? REALTIME_CONFIG.CONNECTION_TIMEOUT);

      socket.once('connect', () => {
        clearTimeout(connectTimeout);

        // Post-connect authentication (backend expects explicit authenticate event)
        if (token) {
          socket!.emit(
            'authenticate',
            { token },
            (response: { success: boolean; error?: string }) => {
              if (options.debug) {
                console.log('[Socket] Auth response:', response);
              }
            }
          );
        }

        updateConnectionState({
          status: CONNECTION_STATUS.CONNECTED,
          isConnected: true,
          isConnecting: false,
          isReconnecting: false,
          reconnectAttempts: 0,
          connectedAt: new Date(),
          error: null,
        });

        if (options.debug) {
          console.log('[Socket] Connected');
        }

        emitInternalEvent(SOCKET_EVENT.CONNECT, {});

        if (options.onConnect) {
          options.onConnect();
        }

        resolve();
      });

      // Map all server-emitted events to internal event system.
      // Backend envelope: { type, event, payload, timestamp }
      // EventPayload shape: { event, data, timestamp, metadata }
      socket.onAny((eventName: string, message: unknown) => {
        if (options.debug) {
          console.log('[Socket] Received:', eventName, message);
        }

        const msg = message as {
          type?: string;
          event?: string;
          payload?: unknown;
          timestamp?: number;
        } | null;

        const resolvedEvent = msg?.event ?? eventName;
        const eventPayload: EventPayload = {
          event: resolvedEvent,
          data: msg?.payload,
          timestamp: msg?.timestamp ?? Date.now(),
          metadata: {},
        };

        emitInternalEvent(resolvedEvent, eventPayload);

        if (options.onMessage) {
          options.onMessage({
            type: 'event',
            event: resolvedEvent,
            data: msg?.payload,
            timestamp: msg?.timestamp,
          } as SocketMessage);
        }
      });

      socket.on('disconnect', (reason: string) => {
        clearTimeout(connectTimeout);

        const wasConnected = connectionState.isConnected;

        updateConnectionState({
          status: CONNECTION_STATUS.DISCONNECTED,
          isConnected: false,
          isConnecting: false,
          disconnectedAt: new Date(),
        });

        if (options.debug) {
          console.log('[Socket] Disconnected:', reason);
        }

        emitInternalEvent(SOCKET_EVENT.DISCONNECT, { reason });

        if (options.onDisconnect) {
          options.onDisconnect(reason);
        }

        // Auto-reconnect unless client initiated the disconnect
        const maxAttempts = options.maxReconnectAttempts ?? REALTIME_CONFIG.MAX_RECONNECT_ATTEMPTS;
        if (
          wasConnected &&
          options.autoReconnect &&
          reason !== 'io client disconnect' &&
          connectionState.reconnectAttempts < maxAttempts
        ) {
          scheduleReconnect();
        }
      });

      socket.on('connect_error', (error: Error) => {
        clearTimeout(connectTimeout);

        if (options.debug) {
          console.error('[Socket] Connection error:', error.message);
        }

        updateConnectionState({
          status: CONNECTION_STATUS.ERROR,
          isConnecting: false,
          error: error.message,
        });

        if (options.onError) {
          options.onError(error);
        }

        reject(error);
      });

      socket.connect();
    } catch (error) {
      updateConnectionState({
        status: CONNECTION_STATUS.ERROR,
        isConnecting: false,
        error: error instanceof Error ? error.message : 'Connection failed',
      });

      if (options.debug) {
        console.error('[Socket] Connection error:', error);
      }

      reject(error);
    }
  });

/**
 * Disconnect from Socket.IO server
 */
export const disconnect = (): void => {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }

  if (socket) {
    updateConnectionState({ status: CONNECTION_STATUS.CLOSING });
    socket.disconnect();
    socket = null;
  }

  updateConnectionState({
    status: CONNECTION_STATUS.DISCONNECTED,
    isConnected: false,
    isConnecting: false,
    isReconnecting: false,
    disconnectedAt: new Date(),
  });

  if (options.debug) {
    console.log('[Socket] Disconnected');
  }
};

/**
 * Reconnect to Socket.IO server
 */
export const reconnect = async (): Promise<void> => {
  if (options.debug) {
    console.log('[Socket] Reconnecting...');
  }

  disconnect();
  await connect();
};

/**
 * Schedule Reconnect with exponential backoff
 */
const scheduleReconnect = (): void => {
  if (reconnectTimer) return;

  const attempts = connectionState.reconnectAttempts;
  const maxAttempts = options.maxReconnectAttempts ?? REALTIME_CONFIG.MAX_RECONNECT_ATTEMPTS;

  if (attempts >= maxAttempts) {
    if (options.debug) {
      console.log('[Socket] Max reconnect attempts reached');
    }
    emitInternalEvent(SOCKET_EVENT.RECONNECT_FAILED, { attempts });
    return;
  }

  updateConnectionState({
    status: CONNECTION_STATUS.RECONNECTING,
    isReconnecting: true,
    reconnectAttempts: attempts + 1,
  });

  // Exponential backoff: 5s, 10s, 20s, 40s, … capped at 60s
  const delay = Math.min(
    (options.reconnectInterval ?? REALTIME_CONFIG.RECONNECT_INTERVAL) * Math.pow(2, attempts),
    60_000
  );

  if (options.debug) {
    console.log(`[Socket] Reconnecting in ${delay}ms (attempt ${attempts + 1}/${maxAttempts})`);
  }

  emitInternalEvent(SOCKET_EVENT.RECONNECT_ATTEMPT, { attempt: attempts + 1, delay });

  reconnectTimer = setTimeout(async () => {
    reconnectTimer = null;

    try {
      await connect();

      if (options.debug) {
        console.log('[Socket] Reconnected successfully');
      }

      emitInternalEvent(SOCKET_EVENT.RECONNECT, {});

      if (options.onReconnect) {
        options.onReconnect();
      }
    } catch (error) {
      if (options.debug) {
        console.error('[Socket] Reconnection failed:', error);
      }

      emitInternalEvent(SOCKET_EVENT.RECONNECT_ERROR, { error });
      scheduleReconnect();
    }
  }, delay);
};

/**
 * Send a structured message to the server
 */
export const send = <T = unknown>(message: SocketMessage<T>): void => {
  if (!socket?.connected) {
    if (options.debug) {
      console.warn('[Socket] Not connected, cannot send message');
    }
    return;
  }

  socket.emit('message', {
    ...message,
    timestamp: message.timestamp || Date.now(),
  });

  if (options.debug) {
    console.log('[Socket] Sent:', message);
  }
};

/**
 * Emit a custom event to the server
 */
export const emit = <T = unknown>(event: string, data: T): void => {
  if (!socket?.connected) {
    if (options.debug) {
      console.warn('[Socket] Not connected, cannot emit event');
    }
    return;
  }

  socket.emit(event, data);
};

/**
 * Subscribe to an event from the server
 *
 * @returns Unsubscribe function
 */
export const on = <T = unknown>(event: string, handler: EventHandler<T>): (() => void) => {
  if (!eventHandlers.has(event)) {
    eventHandlers.set(event, new Set());
  }

  eventHandlers.get(event)!.add(handler as EventHandler);

  return () => {
    off(event, handler as EventHandler);
  };
};

/**
 * Unsubscribe from an event
 */
export const off = (event: string, handler: EventHandler): void => {
  const handlers = eventHandlers.get(event);
  if (handlers) {
    handlers.delete(handler);
    if (handlers.size === 0) {
      eventHandlers.delete(event);
    }
  }
};

/**
 * Get current connection state
 */
export const getConnectionState = (): ConnectionState => {
  return { ...connectionState };
};

/**
 * Is the socket currently connected?
 */
export const isConnected = (): boolean => {
  return connectionState.isConnected;
};

/**
 * Get current latency in milliseconds
 */
export const getLatency = (): number | null => {
  return connectionState.latency;
};

/**
 * Destroy the socket service and clear all state
 */
export const destroy = (): void => {
  disconnect();
  eventHandlers.clear();

  if (options.debug) {
    console.log('[Socket] Destroyed');
  }
};

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

const updateConnectionState = (updates: Partial<ConnectionState>): void => {
  connectionState = { ...connectionState, ...updates };
};

const emitInternalEvent = <T = unknown>(event: string, payload: T): void => {
  const handlers = eventHandlers.get(event);
  if (handlers) {
    handlers.forEach((handler) => {
      try {
        (handler as (p: unknown) => void)(payload);
      } catch (error) {
        console.error(`[Socket] Error in event handler for "${event}":`, error);
      }
    });
  }
};
